import React, { useEffect, useState } from 'react';
import { Box, Button, Modal, IconButton, Tooltip } from '@material-ui/core';
import BuzzTable from '../../../components/BuzzTable/BuzzTable';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';
import useSessionStorageState from '../../../hooks/useSessionStorageState';
import CustomForm from '../../../components/FormRenderer/CustomForm';
import {
  initializeFormData,
  sanitizePayload,
} from '../../../components/FormRenderer/utils';
import CurriculumFrameworkPicker, {
  CURRICULUM_FRAMEWORK_STORAGE_KEY,
  FrameworkRequiredNotice,
} from './CurriculumFrameworkPicker';
import {
  createCurriculumAgeGroup,
  getCurriculumAgeGroups,
  updateCurriculumAgeGroup,
} from '../AkademySlice';

function AkademyCurriculumAgeGroups() {
  const dispatch = useDispatch();
  const { ErrorToast, SuccessToast } = useToast();
  const { handleLoader } = useLoader();

  const [frameworkId, setFrameworkId] = useSessionStorageState(
    CURRICULUM_FRAMEWORK_STORAGE_KEY,
    null
  );
  const [showFormModal, setFormModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({});

  const { curriculumAgeGroups } = useSelector(({ akademyReducer }) => {
    return {
      curriculumAgeGroups: akademyReducer.curriculumAgeGroups,
    };
  });

  useEffect(() => {
    if (!frameworkId) return;

    dispatch(getCurriculumAgeGroups({ frameworkId }));
  }, [frameworkId]);

  // `frameworkId` is create-only — `UpdateCurriculumAgeGroupDTO` accepts only the months.
  const buildFormTemplate = () => [
    {
      id: 'form.minMonth',
      title: 'Min Month',
      slug: 'minMonth',
      type: 'Number',
      required: true,
    },
    {
      id: 'form.maxMonth',
      title: 'Max Month',
      slug: 'maxMonth',
      type: 'Number',
      required: true,
    },
  ];

  const ageGroupColumns = [
    { Header: 'Age Group Id', accessor: '_id' },
    { Header: 'Min Month', accessor: 'minMonth' },
    { Header: 'Max Month', accessor: 'maxMonth' },
    {
      Header: 'Action',
      Cell: (val) => {
        const { _id } = val.cell.row.original;

        return (
          <>
            <IconButton onClick={() => handleEdit(_id)}>
              <EditIcon color="secondary" />
            </IconButton>
            <Tooltip title="Curriculum has no delete — deprecate instead">
              <IconButton disabled>
                <DeleteIcon
                  color="primary"
                  style={{ color: 'grey', opacity: 0.5 }}
                />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];

  const openFormModal = () => setFormModal(true);
  const closeFormModal = () => setFormModal(false);

  const handleEdit = (id) => {
    setSelectedRowId(id);
    const { minMonth, maxMonth } = curriculumAgeGroups.filter(
      ({ _id }) => _id === id
    )[0];
    setFormTemplate(buildFormTemplate());
    setFormData({ _id: id, minMonth, maxMonth });
    openFormModal();
  };

  const handleCreateNew = () => {
    setSelectedRowId(null);
    const template = buildFormTemplate();
    setFormTemplate(template);
    setFormData(initializeFormData(template));
    openFormModal();
  };

  const handleSave = async () => {
    handleLoader(true);

    dispatch(
      selectedRowId === null
        ? createCurriculumAgeGroup(sanitizePayload({ ...formData, frameworkId }))
        : updateCurriculumAgeGroup(formData)
    )
      .unwrap()
      .then(() => {
        SuccessToast(
          selectedRowId === null ? 'Age group created' : 'Age group saved'
        );
        dispatch(getCurriculumAgeGroups({ frameworkId }));
        closeFormModal();
      })
      .catch((error) => {
        console.error('rejected', error);
        ErrorToast(error.message);
      });

    handleLoader(false);
  };

  return (
    <Box>
      <Box style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
        <Button
          onClick={handleCreateNew}
          variant={'contained'}
          color="primary"
          disabled={!frameworkId}>
          Add Age Group
        </Button>
        <Box style={{ marginLeft: 'auto' }}>
          <CurriculumFrameworkPicker
            value={frameworkId}
            onChange={setFrameworkId}
          />
        </Box>
      </Box>

      {!frameworkId ? (
        <FrameworkRequiredNotice>
          Pick a framework to see its age groups.
        </FrameworkRequiredNotice>
      ) : (
        <Box my={2}>
          <BuzzTable
            columns={ageGroupColumns}
            data={curriculumAgeGroups}
            isHandlePagination={true}
          />
        </Box>
      )}

      <Modal open={showFormModal} onClose={closeFormModal}>
        <CustomForm
          formFor={'Age Group'}
          formTemplate={formTemplate}
          formData={formData}
          setFormData={(slug, newValue) => {
            setFormData((state) => {
              state[slug] = newValue;
              return state;
            });
            setFormTemplate((state) => {
              return JSON.parse(JSON.stringify(state));
            });
          }}
          mode={selectedRowId === null ? 'CREATE' : 'EDIT'}
          handleSave={handleSave}
        />
      </Modal>
    </Box>
  );
}

export default AkademyCurriculumAgeGroups;
